// src/pages/IntervalRequests.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { intervalLabel, intervalBadgeClass, formatDateTime, getErrorMessage } from '../utils/helpers';
import { PageWrapper } from './Dashboard';

function locLabel(type) {
  return type === 'detox' ? 'Detox' : 'Residential';
}

export default function IntervalRequests() {
  const { isSupervisor } = useAuth();
  const [activeTab, setActiveTab] = useState('interval'); // 'interval' | 'loc'
  const [requests, setRequests] = useState([]);
  const [locRequests, setLocRequests] = useState([]);
  const [dischargeRequests, setDischargeRequests] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewRequest, setShowNewRequest] = useState(false);
  const [reviewModal, setReviewModal] = useState(null);
  const [reviewType, setReviewType] = useState('interval'); // 'interval' | 'loc'
  const [reviewAction, setReviewAction] = useState('approve');
  const [reviewNotes, setReviewNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [intervalForm, setIntervalForm] = useState({ clientId: '', requestedIntervalMinutes: 30, clinicalReason: '' });
  const [locForm, setLocForm] = useState({ clientId: '', requestedProgramType: 'residential', clinicalReason: '' });
  const [roomRequests, setRoomRequests] = useState([]);
  const [beds, setBeds] = useState([]);
  const [roomForm, setRoomForm] = useState({ clientId: '', requestedRoom: '', reason: '', urgency: 'routine' });
  const [dischargeTypes, setDischargeTypes] = useState([]);
  const [dischargeType, setDischargeType] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [reqRes, locRes, drRes, clientRes, roomRes, bedRes, dtRes] = await Promise.all([
        api.get('/interval-requests'),
        api.get('/loc-requests'),
        api.get('/discharge-requests').catch(() => ({ data: [] })),
        api.get('/clients'),
        api.get('/room-requests').catch(() => ({ data: [] })),
        api.get('/room-requests/beds').catch(() => ({ data: [] })),
        api.get('/discharge-requests/types').catch(() => ({ data: [] }))
      ]);
      setRequests(reqRes.data);
      setLocRequests(locRes.data);
      setDischargeRequests(drRes.data);
      setClients(clientRes.data);
      setRoomRequests(roomRes.data);
      setBeds(bedRes.data);
      setDischargeTypes(dtRes.data);
    } catch (err) {
      console.error('Load error', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitRoomRequest = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      await api.post('/room-requests', roomForm);
      setSuccess('Room change request submitted. A supervisor will review it shortly.');
      setShowNewRequest(false);
      setRoomForm({ clientId: '', requestedRoom: '', reason: '', urgency: 'routine' });
      load();
    } catch (err) { setError(getErrorMessage(err)); }
    finally { setSaving(false); }
  };

  const submitIntervalRequest = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      await api.post('/interval-requests', { ...intervalForm, requestedIntervalMinutes: parseInt(intervalForm.requestedIntervalMinutes) });
      setSuccess('Interval change request submitted. A supervisor will review it shortly.');
      setShowNewRequest(false);
      setIntervalForm({ clientId: '', requestedIntervalMinutes: 30, clinicalReason: '' });
      load();
    } catch (err) { setError(getErrorMessage(err)); }
    finally { setSaving(false); }
  };

  const submitLocRequest = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      await api.post('/loc-requests', locForm);
      setSuccess('Level of care change request submitted. A supervisor will review it shortly.');
      setShowNewRequest(false);
      setLocForm({ clientId: '', requestedProgramType: 'residential', clinicalReason: '' });
      load();
    } catch (err) { setError(getErrorMessage(err)); }
    finally { setSaving(false); }
  };

  const endpointFor = (type) =>
    type === 'interval' ? '/interval-requests'
    : type === 'loc' ? '/loc-requests'
    : type === 'room' ? '/room-requests'
    : '/discharge-requests';

  const handleReview = async () => {
    if (reviewAction === 'deny' && !reviewNotes.trim()) { setError('Please provide a reason for denial.'); return; }
    if (reviewType === 'discharge' && reviewAction === 'approve' && !dischargeType) {
      setError('Select a discharge type before approving.'); return;
    }
    setSaving(true); setError('');
    try {
      const endpoint = endpointFor(reviewType);
      await api.patch(`${endpoint}/${reviewModal.id}/${reviewAction}`, { reviewerNotes: reviewNotes, dischargeType: dischargeType || undefined });
      setReviewModal(null); setReviewNotes(''); setDischargeType(''); load();
    } catch (err) {
      // A room change into an occupied bed is refused unless explicitly
      // overridden — otherwise approving is how two clients end up in one bed.
      const data = err?.response?.data;
      if (data?.requiresForce) {
        const ok = window.confirm(`${data.error}\n\nApprove anyway and place both clients in that bed?`);
        if (ok) {
          try {
            await api.patch(`${endpointFor(reviewType)}/${reviewModal.id}/approve`, { reviewerNotes: reviewNotes, force: true });
            setReviewModal(null); setReviewNotes(''); load();
          } catch (e2) { setError(getErrorMessage(e2)); }
          finally { setSaving(false); }
          return;
        }
      }
      setError(getErrorMessage(err));
    }
    finally { setSaving(false); }
  };

  const dismissRequest = async (id, type) => {
    if (!window.confirm('Dismiss this request?')) return;
    try {
      await api.patch(`${endpointFor(type)}/${id}/deny`, { reviewerNotes: 'Dismissed — client no longer active' });
      load();
    } catch (err) { console.error(err); }
  };

  const selectedIntervalClient = clients.find(c => c.id === intervalForm.clientId);
  const selectedLocClient = clients.find(c => c.id === locForm.clientId);

  const pendingInterval = requests.filter(r => r.status === 'pending');
  const reviewedInterval = requests.filter(r => r.status !== 'pending');
  const pendingLoc = locRequests.filter(r => r.status === 'pending');
  const reviewedLoc = locRequests.filter(r => r.status !== 'pending');
  const pendingDischarge = dischargeRequests.filter(r => r.status === 'pending');
  const reviewedDischarge = dischargeRequests.filter(r => r.status !== 'pending');
  const pendingRoom = roomRequests.filter(r => r.status === 'pending');
  const reviewedRoom = roomRequests.filter(r => r.status !== 'pending');

  const totalPending = pendingInterval.length + pendingLoc.length + pendingDischarge.length;

  const tabStyle = (tab) => ({
    padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 500, fontSize: 13,
    background: activeTab === tab ? 'var(--primary)' : '#F3F4F6',
    color: activeTab === tab ? '#fff' : '#374151'
  });

  return (
    <PageWrapper active="requests" title="Requests">
      <div>
        {success && <div className="alert-bar success" style={{ marginBottom: 12 }}>{success}</div>}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <button style={tabStyle('interval')} onClick={() => { setActiveTab('interval'); setShowNewRequest(false); setError(''); setSuccess(''); }}>
            Interval Changes {pendingInterval.length > 0 && <span style={{ background: '#EF9F27', color: '#fff', borderRadius: 10, padding: '1px 6px', fontSize: 11, marginLeft: 4 }}>{pendingInterval.length}</span>}
          </button>
          <button style={tabStyle('loc')} onClick={() => { setActiveTab('loc'); setShowNewRequest(false); setError(''); setSuccess(''); }}>
            Level of Care {pendingLoc.length > 0 && <span style={{ background: '#EF9F27', color: '#fff', borderRadius: 10, padding: '1px 6px', fontSize: 11, marginLeft: 4 }}>{pendingLoc.length}</span>}
          </button>
          <button style={tabStyle('discharge')} onClick={() => { setActiveTab('discharge'); setShowNewRequest(false); setError(''); setSuccess(''); }}>
            Discharge {pendingDischarge.length > 0 && <span style={{ background: '#EF9F27', color: '#fff', borderRadius: 10, padding: '1px 6px', fontSize: 11, marginLeft: 4 }}>{pendingDischarge.length}</span>}
          </button>
          <button style={tabStyle('room')} onClick={() => { setActiveTab('room'); setShowNewRequest(false); setError(''); setSuccess(''); }}>
            Room Changes {pendingRoom.length > 0 && <span style={{ background: '#EF9F27', color: '#fff', borderRadius: 10, padding: '1px 6px', fontSize: 11, marginLeft: 4 }}>{pendingRoom.length}</span>}
          </button>
        </div>

        {/* New request button */}
        {!showNewRequest && activeTab !== 'discharge' && (
          <button className="btn btn-primary" style={{ marginBottom: 16 }} onClick={() => { setShowNewRequest(true); setSuccess(''); setError(''); }}>
            + Request {activeTab === 'interval' ? 'interval change' : activeTab === 'loc' ? 'level of care change' : 'room change'}
          </button>
        )}
        {activeTab === 'discharge' && (
          <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 16 }}>
            Discharge requests are submitted from the Clients page by clicking <strong>Request discharge</strong> on a client card.
          </div>
        )}

        {/* ── ROOM CHANGE REQUEST FORM ── */}
        {showNewRequest && activeTab === 'room' && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 500, fontSize: 14, marginBottom: 12 }}>New room change request</div>
            <form onSubmit={submitRoomRequest}>
              <div className="field">
                <label>Client</label>
                <select value={roomForm.clientId} onChange={e => setRoomForm(f => ({ ...f, clientId: e.target.value }))} required>
                  <option value="">Select client…</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.first_name} {c.last_name}{c.room_number ? ` — currently ${c.room_number}` : ' — no room assigned'}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label>Move to</label>
                <select value={roomForm.requestedRoom} onChange={e => setRoomForm(f => ({ ...f, requestedRoom: e.target.value }))} required>
                  <option value="">Select a bed…</option>
                  <optgroup label="Open beds">
                    {beds.filter(b => !b.occupied).map(b => (
                      <option key={b.bed} value={b.bed}>{b.bed} — open</option>
                    ))}
                  </optgroup>
                  <optgroup label="Occupied (needs supervisor override)">
                    {beds.filter(b => b.occupied).map(b => (
                      <option key={b.bed} value={b.bed}>{b.bed} — {b.occupants.join(', ')}</option>
                    ))}
                  </optgroup>
                </select>
                <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>
                  {beds.filter(b => !b.occupied).length} of {beds.length} beds open.
                  {beds.some(b => b.conflict) && ' Some beds show more than one client — those need correcting.'}
                </div>
              </div>

              <div className="field">
                <label>Reason</label>
                <textarea
                  value={roomForm.reason}
                  onChange={e => setRoomForm(f => ({ ...f, reason: e.target.value }))}
                  rows={3}
                  placeholder="Why does this client need to move?"
                  required
                />
              </div>

              <div className="field">
                <label>Urgency</label>
                <select value={roomForm.urgency} onChange={e => setRoomForm(f => ({ ...f, urgency: e.target.value }))}>
                  <option value="routine">Routine</option>
                  <option value="urgent">Urgent — safety or conflict</option>
                </select>
              </div>

              {error && <div className="alert-bar danger" style={{ marginBottom: 12 }}>{error}</div>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Submitting…' : 'Submit request'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => { setShowNewRequest(false); setError(''); }}>Cancel</button>
              </div>
            </form>
          </div>
        )}

        {/* ── INTERVAL REQUEST FORM ── */}
        {showNewRequest && activeTab === 'interval' && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 500, fontSize: 14, marginBottom: 12 }}>New interval change request</div>
            <form onSubmit={submitIntervalRequest}>
              <div className="field">
                <label>Client</label>
                <select required value={intervalForm.clientId} onChange={e => setIntervalForm(f => ({ ...f, clientId: e.target.value }))}>
                  <option value="">— Select client —</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name} (currently {intervalLabel(c.current_interval_minutes)})</option>)}
                </select>
              </div>
              {selectedIntervalClient && (
                <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 12, padding: '6px 10px', background: '#F9FAFB', borderRadius: 6 }}>
                  Current interval: <span className={intervalBadgeClass(selectedIntervalClient.current_interval_minutes)}>{intervalLabel(selectedIntervalClient.current_interval_minutes)}</span>
                </div>
              )}
              <div className="field">
                <label>Requested interval</label>
                <select required value={intervalForm.requestedIntervalMinutes} onChange={e => setIntervalForm(f => ({ ...f, requestedIntervalMinutes: e.target.value }))}>
                  <option value={15}>Q15 — every 15 minutes</option>
                  <option value={30}>Q30 — every 30 minutes</option>
                  <option value={60}>Q60 — every hour</option>
                  <option value={180}>Q180 — every 3 hours</option>
                </select>
              </div>
              <div className="field">
                <label>Clinical reason</label>
                <textarea required value={intervalForm.clinicalReason} onChange={e => setIntervalForm(f => ({ ...f, clinicalReason: e.target.value }))} placeholder="Describe the clinical basis for this change..." style={{ minHeight: 80 }} />
              </div>
              {error && <div className="alert-bar danger" style={{ marginBottom: 8 }}>{error}</div>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Submitting...' : 'Submit for approval'}</button>
                <button className="btn btn-secondary" type="button" onClick={() => { setShowNewRequest(false); setError(''); }}>Cancel</button>
              </div>
            </form>
          </div>
        )}

        {/* ── LOC REQUEST FORM ── */}
        {showNewRequest && activeTab === 'loc' && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 500, fontSize: 14, marginBottom: 12 }}>New level of care change request</div>
            <form onSubmit={submitLocRequest}>
              <div className="field">
                <label>Client</label>
                <select required value={locForm.clientId} onChange={e => {
                  const client = clients.find(c => c.id === e.target.value);
                  const otherType = client?.program_type === 'detox' ? 'residential' : 'detox';
                  setLocForm(f => ({ ...f, clientId: e.target.value, requestedProgramType: otherType }));
                }}>
                  <option value="">— Select client —</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name} (currently {locLabel(c.program_type || 'residential')})</option>)}
                </select>
              </div>
              {selectedLocClient && (
                <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 12, padding: '6px 10px', background: '#F9FAFB', borderRadius: 6 }}>
                  Current level of care: <strong>{locLabel(selectedLocClient.program_type || 'residential')}</strong>
                </div>
              )}
              <div className="field">
                <label>Requested level of care</label>
                <select required value={locForm.requestedProgramType} onChange={e => setLocForm(f => ({ ...f, requestedProgramType: e.target.value }))}>
                  <option value="detox">Detox</option>
                  <option value="residential">Residential</option>
                </select>
              </div>
              <div className="field">
                <label>Clinical reason</label>
                <textarea required value={locForm.clinicalReason} onChange={e => setLocForm(f => ({ ...f, clinicalReason: e.target.value }))} placeholder="Describe the clinical basis for this change..." style={{ minHeight: 80 }} />
              </div>
              {error && <div className="alert-bar danger" style={{ marginBottom: 8 }}>{error}</div>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Submitting...' : 'Submit for approval'}</button>
                <button className="btn btn-secondary" type="button" onClick={() => { setShowNewRequest(false); setError(''); }}>Cancel</button>
              </div>
            </form>
          </div>
        )}

        {/* ── INTERVAL REQUESTS LIST ── */}
        {loading ? <div className="spinner" /> : activeTab === 'interval' ? (
          <>
            {pendingInterval.length > 0 && (
              <>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#854F0B', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Pending review ({pendingInterval.length})</div>
                {pendingInterval.map(req => (
                  <RequestCard key={req.id} req={req} type="interval" isSupervisor={isSupervisor}
                    onReview={(action) => { setReviewModal(req); setReviewType('interval'); setReviewAction(action); setReviewNotes(''); setError(''); }}
                    onDismiss={() => dismissRequest(req.id, 'interval')} />
                ))}
                <div className="divider" />
              </>
            )}
            {reviewedInterval.length > 0 && (
              <>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Recent history</div>
                {reviewedInterval.slice(0, 10).map(req => <RequestCard key={req.id} req={req} type="interval" isSupervisor={false} />)}
              </>
            )}
            {requests.length === 0 && <div className="empty-state">No interval change requests yet.</div>}
          </>
        ) : activeTab === 'loc' ? (
          <>
            {pendingLoc.length > 0 && (
              <>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#854F0B', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Pending review ({pendingLoc.length})</div>
                {pendingLoc.map(req => (
                  <RequestCard key={req.id} req={req} type="loc" isSupervisor={isSupervisor}
                    onReview={(action) => { setReviewModal(req); setReviewType('loc'); setReviewAction(action); setReviewNotes(''); setError(''); }}
                    onDismiss={() => dismissRequest(req.id, 'loc')} />
                ))}
                <div className="divider" />
              </>
            )}
            {reviewedLoc.length > 0 && (
              <>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Recent history</div>
                {reviewedLoc.slice(0, 10).map(req => <RequestCard key={req.id} req={req} type="loc" isSupervisor={false} />)}
              </>
            )}
            {locRequests.length === 0 && <div className="empty-state">No level of care change requests yet.</div>}
          </>
        ) : activeTab === 'discharge' ? (
          <>
            {pendingDischarge.length > 0 && (
              <>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#854F0B', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Pending review ({pendingDischarge.length})</div>
                {pendingDischarge.map(req => (
                  <DischargeCard key={req.id} req={req} isSupervisor={isSupervisor}
                    onReview={(action) => { setReviewModal(req); setReviewType('discharge'); setReviewAction(action); setReviewNotes(''); setError(''); }}
                    onDismiss={() => dismissRequest(req.id, 'discharge')} />
                ))}
                <div className="divider" />
              </>
            )}
            {reviewedDischarge.length > 0 && (
              <>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Recent history</div>
                {reviewedDischarge.slice(0, 10).map(req => <DischargeCard key={req.id} req={req} isSupervisor={false} />)}
              </>
            )}
            {dischargeRequests.length === 0 && <div className="empty-state">No discharge requests yet.</div>}
          </>
        ) : activeTab === 'room' ? (
          <>
            {pendingRoom.length > 0 && (
              <>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#854F0B', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Pending review ({pendingRoom.length})</div>
                {pendingRoom.map(req => (
                  <RoomCard key={req.id} req={req} beds={beds} isSupervisor={isSupervisor}
                    onReview={(action) => { setReviewModal(req); setReviewType('room'); setReviewAction(action); setReviewNotes(''); setError(''); }} />
                ))}
                <div className="divider" />
              </>
            )}
            {reviewedRoom.length > 0 && (
              <>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Recent history</div>
                {reviewedRoom.slice(0, 10).map(req => <RoomCard key={req.id} req={req} beds={beds} isSupervisor={false} />)}
              </>
            )}
            {roomRequests.length === 0 && <div className="empty-state">No room change requests yet.</div>}
          </>
        ) : null}
      </div>

      {/* Review modal */}
      {reviewModal && (
        <div className="modal-overlay" onClick={() => setReviewModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{reviewAction === 'approve' ? 'Approve request' : 'Deny request'}</h2>
            <div style={{ fontSize: 13, color: '#9CA3AF', marginBottom: 12 }}>
              {reviewType === 'discharge'
                ? `${reviewModal.first_name} ${reviewModal.last_name}${reviewModal.room_number ? ' · Room ' + reviewModal.room_number : ''}`
                : reviewModal.client_name
              }
              {reviewType !== 'discharge' && <> · {reviewType === 'interval'
                ? `${intervalLabel(reviewModal.current_interval_minutes)} → ${intervalLabel(reviewModal.requested_interval_minutes)}`
                : reviewType === 'room'
                ? `${reviewModal.current_room || 'unassigned'} → ${reviewModal.requested_room}`
                : `${locLabel(reviewModal.current_program_type)} → ${locLabel(reviewModal.requested_program_type)}`
              }</>}<br />
              Requested by {reviewModal.requested_by_name}
            </div>
            <div style={{ fontSize: 13, color: '#4B5563', marginBottom: 12, padding: '8px 10px', background: '#F9FAFB', borderRadius: 6 }}>
              "{reviewType === 'discharge' ? reviewModal.reason : reviewType === 'room' ? reviewModal.reason : reviewModal.clinical_reason}"
            </div>
            {reviewType === 'discharge' && reviewAction === 'approve' && (
              <>
                <div className="field">
                  <label>Discharge type <span style={{ color: 'var(--red)' }}>· required</span></label>
                  <select value={dischargeType} onChange={e => setDischargeType(e.target.value)}>
                    <option value="">Select type…</option>
                    {dischargeTypes.map(t => (
                      <option key={t.code} value={t.code}>{t.label}</option>
                    ))}
                  </select>
                  {dischargeType && dischargeTypes.find(t => t.code === dischargeType)?.description && (
                    <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>
                      {dischargeTypes.find(t => t.code === dischargeType).description}
                    </div>
                  )}
                </div>
                <div className="alert-bar warning" style={{ marginBottom: 12, padding: '8px 10px', background: '#FFF3E0', color: '#92400E', borderRadius: 6, fontSize: 13 }}>
                  ⚠ Approving will immediately discharge this client from active census.
                </div>
              </>
            )}
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <button className="btn btn-primary btn-sm" style={{ background: reviewAction === 'approve' ? '#1D9E75' : undefined }} onClick={() => setReviewAction('approve')}>Approve</button>
              <button className="btn btn-danger btn-sm" onClick={() => setReviewAction('deny')}>Deny</button>
            </div>
            <div className="field">
              <label>{reviewAction === 'deny' ? 'Reason for denial (required)' : 'Notes (optional)'}</label>
              <textarea value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} placeholder={reviewAction === 'deny' ? 'Explain why this request is being denied...' : 'Optional note to the requester...'} />
            </div>
            {error && <div className="alert-bar danger" style={{ marginBottom: 8 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className={`btn ${reviewAction === 'approve' ? 'btn-primary' : 'btn-danger'}`} onClick={handleReview} disabled={saving}>
                {saving ? 'Saving...' : reviewAction === 'approve' ? 'Confirm approval' : 'Confirm denial'}
              </button>
              <button className="btn btn-secondary" onClick={() => setReviewModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </PageWrapper>
  );
}

function RequestCard({ req, type, isSupervisor, onReview, onDismiss }) {
  const statusStyles = {
    pending: { background: '#FAEEDA', color: '#854F0B' },
    approved: { background: '#E1F5EE', color: '#0F6E56' },
    denied: { background: '#FCEBEB', color: '#A32D2D' }
  };

  const borderColor = req.status === 'pending' ? '#EF9F27' : req.status === 'approved' ? '#1D9E75' : '#E24B4A';

  const changeLabel = type === 'interval'
    ? <><span className={intervalBadgeClass(req.current_interval_minutes)}>{intervalLabel(req.current_interval_minutes)}</span><span style={{ margin: '0 6px' }}>→</span><span className={intervalBadgeClass(req.requested_interval_minutes)}>{intervalLabel(req.requested_interval_minutes)}</span></>
    : <><strong>{locLabel(req.current_program_type)}</strong><span style={{ margin: '0 6px' }}>→</span><strong>{locLabel(req.requested_program_type)}</strong></>;

  return (
    <div className="card" style={{ borderLeft: `3px solid ${borderColor}`, borderRadius: '0 12px 12px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
        <div>
          <div style={{ fontWeight: 500, fontSize: 14 }}>{req.client_name}</div>
          <div style={{ fontSize: 12, color: '#9CA3AF' }}>{changeLabel}</div>
        </div>
        <span className="badge" style={statusStyles[req.status]}>{req.status}</span>
      </div>
      <div style={{ fontSize: 12, color: '#4B5563', marginBottom: 6 }}>"{req.clinical_reason}"</div>
      <div style={{ fontSize: 11, color: '#9CA3AF' }}>
        Requested by {req.requested_by_name} · {formatDateTime(req.created_at)}
      </div>
      {req.reviewed_by_name && (
        <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>
          {req.status === 'approved' ? 'Approved' : 'Denied'} by {req.reviewed_by_name} · {formatDateTime(req.reviewed_at)}
          {req.reviewer_notes && ` · "${req.reviewer_notes}"`}
        </div>
      )}
      {isSupervisor && req.status === 'pending' && (
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <button className="btn btn-primary btn-sm" onClick={() => onReview('approve')}>Approve</button>
          <button className="btn btn-danger btn-sm" onClick={() => onReview('deny')}>Deny</button>
          {onDismiss && <button className="btn btn-secondary btn-sm" onClick={onDismiss}>Dismiss</button>}
        </div>
      )}
    </div>
  );
}

function DischargeCard({ req, isSupervisor, onReview, onDismiss }) {
  const statusStyles = {
    pending: { background: '#FAEEDA', color: '#854F0B' },
    approved: { background: '#E1F5EE', color: '#0F6E56' },
    denied: { background: '#FCEBEB', color: '#A32D2D' }
  };
  const borderColor = req.status === 'pending' ? '#EF9F27' : req.status === 'approved' ? '#1D9E75' : '#E24B4A';

  return (
    <div className="card" style={{ borderLeft: `3px solid ${borderColor}`, borderRadius: '0 12px 12px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
        <div>
          <div style={{ fontWeight: 500, fontSize: 14 }}>{req.first_name} {req.last_name}</div>
          <div style={{ fontSize: 12, color: '#9CA3AF' }}>
            Discharge request{req.room_number ? ` · Room ${req.room_number}` : ''}
          </div>
        </div>
        <span className="badge" style={statusStyles[req.status]}>{req.status}</span>
      </div>
      <div style={{ fontSize: 12, color: '#4B5563', marginBottom: 6 }}>"{req.reason}"</div>
      <div style={{ fontSize: 11, color: '#9CA3AF' }}>
        Requested by {req.requested_by_name} · {formatDateTime(req.created_at)}
      </div>
      {req.reviewed_by_name && (
        <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>
          {req.status === 'approved' ? 'Approved' : 'Denied'} by {req.reviewed_by_name} · {formatDateTime(req.reviewed_at)}
          {req.reviewer_notes && ` · "${req.reviewer_notes}"`}
        </div>
      )}
      {isSupervisor && req.status === 'pending' && (
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <button className="btn btn-primary btn-sm" onClick={() => onReview('approve')}>Approve &amp; discharge</button>
          <button className="btn btn-danger btn-sm" onClick={() => onReview('deny')}>Deny</button>
          {onDismiss && <button className="btn btn-secondary btn-sm" onClick={onDismiss}>Dismiss</button>}
        </div>
      )}
    </div>
  );
}

function RoomCard({ req, beds, isSupervisor, onReview }) {
  const pending = req.status === 'pending';
  const urgent = req.urgency === 'urgent';
  const target = (beds || []).find(b => b.bed === req.requested_room);
  const stillOccupied = pending && target && target.occupied;

  const statusColor = req.status === 'approved' ? '#0F6E56'
    : req.status === 'denied' ? '#A32D2D'
    : req.status === 'cancelled' ? '#9CA3AF' : '#854F0B';

  return (
    <div className="card" style={{ marginBottom: 10, borderLeft: urgent && pending ? '3px solid #A32D2D' : undefined }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>
            {req.client_name}
            {urgent && pending && (
              <span className="badge" style={{ background: 'var(--red-light)', color: 'var(--red)', marginLeft: 8 }}>Urgent</span>
            )}
          </div>
          <div style={{ fontSize: 13, color: '#374151', marginTop: 4 }}>
            {req.current_room || 'unassigned'} → <strong>{req.requested_room}</strong>
          </div>
          <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>{req.reason}</div>
          <div className="le-time" style={{ marginTop: 6 }}>
            Requested by {req.requested_by_name} · {formatDateTime(req.created_at)}
          </div>
          {req.reviewed_by_name && (
            <div className="le-time">
              {req.status === 'approved' ? 'Approved' : req.status === 'denied' ? 'Denied' : 'Reviewed'} by {req.reviewed_by_name}
              {req.reviewer_notes ? ` — ${req.reviewer_notes}` : ''}
            </div>
          )}
        </div>
        <div style={{ textAlign: 'right' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: statusColor, textTransform: 'capitalize' }}>{req.status}</span>
        </div>
      </div>

      {stillOccupied && (
        <div style={{ fontSize: 12, color: '#854F0B', marginTop: 8 }}>
          Bed {req.requested_room} is currently occupied by {target.occupants.join(', ')} — approving will need an override.
        </div>
      )}

      {pending && isSupervisor && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button className="btn btn-primary btn-sm" onClick={() => onReview('approve')}>Approve</button>
          <button className="btn btn-secondary btn-sm" onClick={() => onReview('deny')}>Deny</button>
        </div>
      )}
    </div>
  );
}
